// Pricing migration 32 (ADO-3985), T9: the multi-buy deals batch-read client.
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for Part A's endpoint (D-1),
// `GET /api/v1/multibuy-deals?entry=<kind>:<depotCode>&skus=<csv>[&customerAccountCode=<code>]`.
// Built as `price-quotes.client.ts` is: requests are chunked to the server cap, each chunk
// succeeds or fails on its own, and the read never rejects. A chunk is asked once more only when
// it fails with a 5xx, a network error, or a body that is not JSON or not an array. A 403
// answers every SKU in the chunk with no deals. A chunk that fails twice, or once with another
// 4xx or after the read is aborted, reports its SKUs unread.
//
// LOCK-STEP: the wire types below are a copy of order-capture's `MultibuyDealsDto`
// (`multibuy-deals.dto.ts`), and the request follows its validation
// (`multibuy-deals.validation.ts`: row 16's 100-SKU cap and account rule); checked against both
// on 2026-11-03. This client is the only file that knows the request and response shape.
//
// Parsing is lenient (AC6: nothing is guessed): a malformed optional value is left out, a
// malformed tier or product is dropped, and a deal that has no code, no usable progress, or does
// not fit its `kind` (a `FREE_UNITS` deal left with no tier giving free units, a `DESCRIPTION_ONLY`
// deal with no description, any other kind) is dropped while the product's other deals stay. The
// kind is the server's: this client never decides from the tiers how a deal is shown. A product
// entry that
// cannot be read at all, or a SKU the array does not answer, is reported unread at once: a
// well-formed array is never asked for again, whatever its entries hold.

import { withEntry } from './catalogue-entry';
import { API_PREFIX, apiFetch } from './shoplink-api';

export interface MultibuyDealTier {
  readonly buyQuantity: number;
  readonly freeQuantity: number;
}

/** `RELATED` or `FREE_AND_RELATED`; a `Free Item` product is never listed. */
export type MultibuyDealProductRole = 'RELATED' | 'FREE_AND_RELATED';

export interface MultibuyDealProduct {
  readonly skuCode: string;
  readonly role: MultibuyDealProductRole;
}

/** The entry's cart against the deal (D-2), worked out by the pricing module, never here. */
export interface MultibuyDealProgress {
  readonly pooledQuantity: number;
  readonly freeUnitsAtCurrent: number;
  readonly nextThreshold?: number;
  readonly unitsToNext?: number;
}

interface MultibuyDealFacts {
  readonly multibuyOfferCode: string;
  /** The start to show, `YYYY-MM-DD`: the deal's own; never sent on WOW. */
  readonly displayFrom?: string;
  /** The end to show, `YYYY-MM-DD`: the deal's own, or on WOW the WOW window's end. */
  readonly displayTo?: string;
  readonly products: readonly MultibuyDealProduct[];
  readonly progress: MultibuyDealProgress;
}

/** A deal shown by its tiers ("Buy 4, get 1 free"). */
export interface FreeUnitsMultibuyDeal extends MultibuyDealFacts {
  readonly kind: 'FREE_UNITS';
  /** The feed's description, the title on the product page and in the dialog. */
  readonly description?: string;
  /** Buy ascending, then free; never empty, each tier giving free units. */
  readonly tiers: readonly MultibuyDealTier[];
}

/** A deal no tier of which gives free units: the strip's text is its description. */
export interface DescriptionOnlyMultibuyDeal extends MultibuyDealFacts {
  readonly kind: 'DESCRIPTION_ONLY';
  readonly description: string;
  readonly tiers: readonly [];
}

/** One live deal reaching a product (lock-step with `MultibuyDealDto`). An absent value is
 *  left out, never sent as `null`. */
export type MultibuyDeal = FreeUnitsMultibuyDeal | DescriptionOnlyMultibuyDeal;

/** One product's answer (lock-step with `MultibuyDealsDto`). The wire's optional `reason` (why
 *  a product's deals were not read) is not kept: the screen shows such a product as "no deal"
 *  (AC6) whatever the reason. */
export interface MultibuyDealsForSku {
  readonly skuCode: string;
  readonly deals: readonly MultibuyDeal[];
}

export const MULTIBUY_DEALS_PATH = `${API_PREFIX}/multibuy-deals`;

/** Row 16's cap on distinct SKUs per request, which the deal read shares (D-1). */
export const MAX_MULTIBUY_DEALS_BATCH_SIZE = 100;

/**
 * The outcome of one batch read, per chunk and possibly partial, as the price read's: `answers`
 * carries an entry for every SKU a successful chunk answered (with deals or without), and
 * `unreadSkus` names the SKUs nobody answered.
 */
export interface MultibuyDealsReadResult {
  readonly answers: readonly MultibuyDealsForSku[];
  readonly unreadSkus: readonly string[];
}

/** The `customerAccountCode` query parameter, or `''` when there is no account to name. */
function accountParam(customerAccountCode: string | null): string {
  const code = customerAccountCode?.trim() ?? '';
  return code === '' ? '' : `&customerAccountCode=${encodeURIComponent(code)}`;
}

function isWholeNumber(value: unknown, minimum: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= minimum;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseTier(value: unknown): MultibuyDealTier | null {
  const tier = asRecord(value);
  if (tier === null) return null;
  const { buyQuantity, freeQuantity } = tier;
  if (!isWholeNumber(buyQuantity, 1) || !isWholeNumber(freeQuantity, 1)) return null;
  return { buyQuantity, freeQuantity };
}

function parseProduct(value: unknown): MultibuyDealProduct | null {
  const product = asRecord(value);
  if (product === null) return null;
  const { skuCode, role } = product;
  if (!isNonEmptyString(skuCode) || (role !== 'RELATED' && role !== 'FREE_AND_RELATED'))
    return null;
  return { skuCode, role };
}

function parseProgress(value: unknown): MultibuyDealProgress | null {
  const progress = asRecord(value);
  if (progress === null) return null;
  const { pooledQuantity, freeUnitsAtCurrent, nextThreshold, unitsToNext } = progress;
  if (!isWholeNumber(pooledQuantity, 0) || !isWholeNumber(freeUnitsAtCurrent, 0)) return null;
  return {
    pooledQuantity,
    freeUnitsAtCurrent,
    ...(isWholeNumber(nextThreshold, 1) ? { nextThreshold } : {}),
    ...(isWholeNumber(unitsToNext, 1) ? { unitsToNext } : {}),
  };
}

function isNonBlankString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

/** Parse one deal, or `null` when it cannot be shown. */
function parseDeal(value: unknown): MultibuyDeal | null {
  const deal = asRecord(value);
  if (deal === null) return null;
  const { multibuyOfferCode, kind, description, displayFrom, displayTo } = deal;
  if (!isNonEmptyString(multibuyOfferCode)) return null;
  const progress = parseProgress(deal['progress']);
  if (progress === null) return null;
  const products = Array.isArray(deal['products'])
    ? deal['products'].flatMap((product) => parseProduct(product) ?? [])
    : [];
  const facts = {
    multibuyOfferCode,
    ...(isNonEmptyString(displayFrom) ? { displayFrom } : {}),
    ...(isNonEmptyString(displayTo) ? { displayTo } : {}),
    products,
    progress,
  };
  if (kind === 'DESCRIPTION_ONLY') {
    if (!isNonBlankString(description)) return null;
    return { kind, description, tiers: [], ...facts };
  }
  if (kind !== 'FREE_UNITS') return null;
  const tiers = Array.isArray(deal['tiers'])
    ? deal['tiers'].flatMap((tier) => parseTier(tier) ?? [])
    : [];
  if (tiers.length === 0) return null;
  return {
    kind,
    ...(isNonEmptyString(description) ? { description } : {}),
    tiers,
    ...facts,
  };
}

/** Parse one product's entry, or `null` when it cannot be used at all. */
function parseAnswer(value: unknown): MultibuyDealsForSku | null {
  const entry = asRecord(value);
  if (entry === null) return null;
  const { skuCode, deals } = entry;
  if (!isNonEmptyString(skuCode) || !Array.isArray(deals)) return null;
  return { skuCode, deals: deals.flatMap((deal) => parseDeal(deal) ?? []) };
}

/** A chunk failure worth asking again for: a 5xx, a network error, or a body that is not JSON
 *  or not an array. */
class RetryableChunkError extends Error {}

/** Fetch one chunk. A 403 (the entry is not the caller's) answers every SKU in it with no deals.
 *  Throws on a transport failure, any other non-2xx or a body that is not an array. */
async function fetchChunk(
  entryKey: string,
  customerAccountCode: string | null,
  chunk: readonly string[],
  signal: AbortSignal | undefined,
): Promise<MultibuyDealsReadResult> {
  const url = withEntry(
    `${MULTIBUY_DEALS_PATH}?skus=${encodeURIComponent(chunk.join(','))}${accountParam(customerAccountCode)}`,
    entryKey,
  );
  let res: Response;
  try {
    res = await apiFetch(url, signal === undefined ? {} : { signal });
  } catch (error) {
    throw new RetryableChunkError(error instanceof Error ? error.message : 'Network error');
  }
  if (res.status === 403) {
    return {
      answers: chunk.map((skuCode) => ({ skuCode, deals: [] })),
      unreadSkus: [],
    };
  }
  if (res.status >= 500) throw new RetryableChunkError(`Failed to load deals (${res.status})`);
  if (!res.ok) throw new Error(`Failed to load deals (${res.status})`);
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    throw new RetryableChunkError('Malformed multi-buy deals response');
  }
  if (!Array.isArray(body)) throw new RetryableChunkError('Malformed multi-buy deals response');

  const asked = new Set(chunk);
  const answered = new Map<string, MultibuyDealsForSku>();
  for (const value of body) {
    const answer = parseAnswer(value);
    // The first answer per asked SKU is the one used; anything else is ignored.
    if (answer === null || !asked.has(answer.skuCode) || answered.has(answer.skuCode)) continue;
    answered.set(answer.skuCode, answer);
  }
  return {
    answers: chunk.flatMap((sku) => answered.get(sku) ?? []),
    unreadSkus: chunk.filter((sku) => !answered.has(sku)),
  };
}

/**
 * Fetch the live multi-buy deals of `skuCodes` under the ordering entry `entryKey` for the
 * trading account `customerAccountCode` (`null` sends none), as `fetchPriceQuotes` does. Blank
 * and duplicate SKUs are dropped, the rest chunked at {@link MAX_MULTIBUY_DEALS_BATCH_SIZE}.
 * Never rejects: a chunk that fails twice (or once with a 4xx, or aborted) reports its SKUs as
 * unread.
 */
export async function fetchMultibuyDeals(
  entryKey: string,
  customerAccountCode: string | null,
  skuCodes: readonly string[],
  signal?: AbortSignal,
): Promise<MultibuyDealsReadResult> {
  const unique = [...new Set(skuCodes)].filter((sku) => sku !== '');
  if (unique.length === 0) return { answers: [], unreadSkus: [] };

  const chunks: string[][] = [];
  for (let index = 0; index < unique.length; index += MAX_MULTIBUY_DEALS_BATCH_SIZE) {
    chunks.push(unique.slice(index, index + MAX_MULTIBUY_DEALS_BATCH_SIZE));
  }

  const results = await Promise.all(
    chunks.map(async (chunk): Promise<MultibuyDealsReadResult> => {
      for (let attempt = 1; ; attempt += 1) {
        try {
          return await fetchChunk(entryKey, customerAccountCode, chunk, signal);
        } catch (error) {
          const retryable = error instanceof RetryableChunkError && signal?.aborted !== true;
          if (attempt === 2 || !retryable) return { answers: [], unreadSkus: chunk };
        }
      }
    }),
  );
  return {
    answers: results.flatMap((result) => result.answers),
    unreadSkus: results.flatMap((result) => result.unreadSkus),
  };
}
